import clsx from 'clsx'
import { CircleAlert, CircleCheck, CircleX, Info, X } from 'lucide-react'
import type { HTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'

type AlertType = 'info' | 'success' | 'warning' | 'error'
type AlertVariant = 'simple' | 'solid' | 'soft'

type AlertProps = HTMLAttributes<HTMLDivElement> & {
    type?: AlertType
    variant?: AlertVariant
    icon?: ReactNode
    onCloseClick?: () => void
}

const SimpleAlert = forwardRef<HTMLDivElement, Omit<AlertProps, 'variant'>>((props, ref) => {
    const { children, className, onCloseClick, type = 'info', ...otherProps } = props

    return (
        <div
            className={clsx(
                'rounded-xl border border-neutral-200 bg-white shadow-md dark:border-neutral-700 dark:bg-neutral-800 dark:shadow-neutral-900',
                className,
            )}
            ref={ref}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            {...otherProps}
        >
            <div className="flex p-4">
                <div className="flex-shrink-0">
                    {type === 'info' && <Info className="h-6 w-6 fill-primary-600 text-white dark:text-neutral-800" />}
                    {type === 'success' && (
                        <CircleCheck className="h-6 w-6 fill-success-600 text-white dark:text-neutral-800" />
                    )}
                    {type === 'warning' && (
                        <CircleAlert className="h-6 w-6 fill-warning-600 text-white dark:text-neutral-800" />
                    )}
                    {type === 'error' && (
                        <CircleX className="h-6 w-6 fill-danger-600 text-white dark:text-neutral-800" />
                    )}
                </div>
                <div className="ms-3 text-sm text-neutral-700 dark:text-neutral-400">{children}</div>
                {onCloseClick ? (
                    <div className="ms-auto shrink-0">
                        <button
                            onClick={onCloseClick}
                            type="button"
                            className="inline-flex size-5 flex-shrink-0 items-center justify-center rounded-lg text-neutral-800 opacity-50 hover:opacity-100 focus:opacity-100 focus:outline-none dark:text-white"
                        >
                            <span className="sr-only">Close</span>
                            <X className="size-4 flex-shrink-0" />
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    )
})

SimpleAlert.displayName = 'SimpleAlert'

const SolidAlert = forwardRef<HTMLDivElement, Omit<AlertProps, 'variant'>>((props, ref) => {
    const { children, className, onCloseClick, type = 'info', ...otherProps } = props

    return (
        <div
            className={clsx(
                'pointer-events-auto rounded-xl text-sm text-neutral-50 shadow-md dark:shadow-none',
                { 'bg-primary-600': type === 'info' },
                { 'bg-success-600': type === 'success' },
                { 'bg-warning-600': type === 'warning' },
                { 'bg-danger-600': type === 'error' },
                className,
            )}
            ref={ref}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            {...otherProps}
        >
            <div className="flex p-4">
                <div className="flex-shrink-0">
                    {type === 'info' && <Info className="h-6 w-6 stroke-neutral-50" />}
                    {type === 'success' && <CircleCheck className="h-6 w-6 stroke-neutral-50" />}
                    {type === 'warning' && <CircleAlert className="h-6 w-6 stroke-neutral-50" />}
                    {type === 'error' && <CircleX className="h-6 w-6 stroke-neutral-50" />}
                </div>
                <div className="ms-3 text-sm">{children}</div>
                {onCloseClick ? (
                    <div className="ms-auto shrink-0">
                        <button
                            onClick={onCloseClick}
                            type="button"
                            className="inline-flex size-5 flex-shrink-0 items-center justify-center rounded-lg text-white opacity-50 hover:text-white hover:opacity-100 focus:opacity-100 focus:outline-none"
                        >
                            <span className="sr-only">Close</span>
                            <X className="size-4 flex-shrink-0" />
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    )
})

SolidAlert.displayName = 'SolidAlert'

const SoftAlert = forwardRef<HTMLDivElement, Omit<AlertProps, 'variant'>>((props, ref) => {
    const { children, className, onCloseClick, type = 'info', ...otherProps } = props

    return (
        <div
            className={clsx(
                'rounded-lg border text-sm',
                {
                    'border-primary-600 bg-primary-100 text-primary-800 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-500':
                        type === 'info',
                },
                {
                    'border-success-600 bg-success-100 text-success-800 dark:border-success-800 dark:bg-success-950 dark:text-success-500':
                        type === 'success',
                },
                {
                    'border-warning-600 bg-warning-100 text-warning-800 dark:border-warning-800 dark:bg-warning-950 dark:text-warning-500':
                        type === 'warning',
                },
                {
                    'border-danger-600 bg-danger-100 text-danger-800 dark:border-danger-800 dark:bg-danger-950 dark:text-danger-500':
                        type === 'error',
                },
                className,
            )}
            ref={ref}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            {...otherProps}
        >
            <div className="flex p-4">
                <div className="flex-shrink-0">
                    {type === 'info' && <Info className="h-6 w-6 text-primary-600" />}
                    {type === 'success' && <CircleCheck className="h-6 w-6 text-success-600" />}
                    {type === 'warning' && <CircleAlert className="h-6 w-6 text-warning-600" />}
                    {type === 'error' && <CircleX className="h-6 w-6 text-danger-600" />}
                </div>
                <div className="ms-3 text-sm">{children}</div>
                {onCloseClick ? (
                    <div className="ms-auto shrink-0">
                        <button
                            onClick={onCloseClick}
                            type="button"
                            className={clsx(
                                { 'text-primary-800 dark:text-primary-200': type === 'info' },
                                { 'text-danger-800 dark:text-danger-200': type === 'error' },
                                { 'text-warning-800 dark:text-warning-200': type === 'warning' },
                                { 'text-success-800 dark:text-success-200': type === 'success' },
                                'inline-flex size-5 flex-shrink-0 items-center justify-center rounded-lg opacity-50 hover:opacity-100 focus:opacity-100 focus:outline-none',
                            )}
                        >
                            <span className="sr-only">Close</span>
                            <X className="size-4 flex-shrink-0" />
                        </button>
                    </div>
                ) : null}
            </div>
        </div>
    )
})

SoftAlert.displayName = 'SoftAlert'

const Alert = forwardRef<HTMLDivElement, AlertProps>((props, ref) => {
    const { variant, ...otherProps } = props

    switch (variant) {
        case 'simple':
            return <SimpleAlert {...otherProps} ref={ref} />
        case 'soft':
            return <SoftAlert {...otherProps} ref={ref} />
        default:
            return <SolidAlert {...otherProps} ref={ref} />
    }
})

Alert.displayName = 'Alert'

export { Alert, SimpleAlert, SoftAlert, SolidAlert, type AlertProps, type AlertType, type AlertVariant }
